import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { MediaPlaceholder } from '@/components/patterns/media-placeholder'
import ProductCatalogNav from '@/components/products/ProductCatalogNav'
import { IndexTableSearchBox } from '@/components/tables/IndexTable'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { addOnSetQueries, useAddOnSets } from '@/lib/services/add-on-sets/addOnSets.query'
import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { Camera, ChevronRight, Gift, LibraryBig, PackagePlus, Plus } from 'lucide-react'
import { useState } from 'react'

export const Route = createFileRoute('/_authenticated/$orgId/products/add-on-sets/')({
  beforeLoad: ({ context }) => {
    if (!context.ability.can('list', 'products')) throw notFound()
  },
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(
      addOnSetQueries.index({ organizationId: params.orgId })
    )

    return { isReadOnly: !context.ability.can('edit', 'products') }
  },
  component: AddOnSetsIndexPage,
})

const maxVisibleAddOnChips = 6
const maxVisibleCollectionChips = 5

function getPreviewMedia(addOnSet) {
  return (addOnSet?.add_ons ?? [])
    .map((addOn) => addOn?.media?.[0])
    .filter((media) => media?.url)
    .slice(0, 3)
}

function AddOnSetPreview({ addOnSet }) {
  const mediaItems = getPreviewMedia(addOnSet)

  if (!mediaItems.length) {
    return <MediaPlaceholder icon={Camera} size="xs" />
  }

  return (
    <div className="flex h-12 shrink-0 items-center -space-x-2">
      {mediaItems.map((media) => (
        <div
          key={media.id ?? media.url}
          className="size-12 overflow-hidden rounded-xl border border-background bg-muted shadow-sm"
        >
          <img
            src={media.url}
            alt={media.name ?? 'Add-on image'}
            className="block size-full object-cover object-center"
            loading="lazy"
          />
        </div>
      ))}
    </div>
  )
}

function getAddOnMedia(addOn) {
  return (addOn?.media ?? []).find((media) => media?.url) ?? null
}

function AddOnChip({ item, withMedia = false }) {
  const media = withMedia ? getAddOnMedia(item) : null

  return (
    <Badge variant="brand" size={withMedia ? 'mediaChip' : 'chip'}>
      {withMedia ? (
        media ? (
          <img
            src={media.url}
            alt=""
            className="size-5 rounded-full border border-background object-cover"
            loading="lazy"
          />
        ) : (
          <span className="inline-flex size-5 items-center justify-center rounded-full bg-background/70 text-interaction">
            <Gift className="size-3" aria-hidden="true" />
          </span>
        )
      ) : null}
      {item.name}
    </Badge>
  )
}

function AddOnOverflowChip({ children }) {
  return (
    <Badge variant="muted" size="xs">
      {children}
    </Badge>
  )
}

function AddOnSetChipList({ icon: Icon, label, items, maxVisible, emptyText }) {
  const shownItems = items.slice(0, maxVisible)
  const hiddenCount = items.length - shownItems.length

  return (
    <div className="grid gap-2 sm:grid-cols-[7rem_minmax(0,1fr)] sm:items-start">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="size-3.5" aria-hidden="true" />
        {label}
      </div>
      <div className="flex min-w-0 flex-wrap gap-1.5">
        {shownItems.length ? (
          shownItems.map((item) => <AddOnChip key={item.id ?? item.name} item={item} />)
        ) : (
          <Typography variant="label" tone="muted" italic>
            {emptyText}
          </Typography>
        )}
        {hiddenCount > 0 ? <AddOnOverflowChip>+{hiddenCount} more</AddOnOverflowChip> : null}
      </div>
    </div>
  )
}

function AddOnSetCard({ addOnSet, path }) {
  const addOns = addOnSet?.add_ons ?? []
  const collections = addOnSet?.applies_to_collections ?? []

  return (
    <Link
      {...path.addOnSets.show(addOnSet.id).link}
      className="group block overflow-hidden rounded-lg border border-border/70 bg-card transition hover:border-primary/25 hover:bg-muted/20"
    >
      <div className="flex min-w-0 items-center gap-3 border-b border-border/60 bg-muted/20 px-4 py-3">
        <AddOnSetPreview addOnSet={addOnSet} />
        <div className="min-w-0 flex-1">
          <Typography variant="cardTitle" className="pb-0">
            {addOnSet.name}
          </Typography>
          {addOnSet.description ? (
            <Typography variant="supporting" className="block">
              {addOnSet.description}
            </Typography>
          ) : null}
        </div>
        <ChevronRight className="size-5 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-interaction" />
      </div>
      <div className="space-y-3 px-4 py-3">
        <AddOnSetChipList
          icon={Gift}
          label="Offer these"
          items={addOns}
          maxVisible={maxVisibleAddOnChips}
          emptyText="No add-ons selected."
        />
        <AddOnSetChipList
          icon={LibraryBig}
          label="Show them on"
          items={collections}
          maxVisible={maxVisibleCollectionChips}
          emptyText="No product collections selected."
        />
      </div>
    </Link>
  )
}

function getUniqueAddOns(addOnSets) {
  const addOnsByKey = new Map()

  addOnSets.forEach((addOnSet) => {
    const addOns = addOnSet?.add_ons ?? []

    addOns.forEach((addOn) => {
      const key = addOn?.id ?? addOn?.name

      if (key && !addOnsByKey.has(key)) {
        addOnsByKey.set(key, addOn)
      }
    })
  })

  return [...addOnsByKey.values()]
}

function ManageAddOnProductsPanel({ addOns, path }) {
  const visibleAddOns = addOns.slice(0, maxVisibleAddOnChips)
  const hiddenCount = addOns.length - visibleAddOns.length

  return (
    <section className="rounded-lg border border-border/70 bg-card p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/10 text-interaction">
              <Gift className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <Typography variant="cardTitle" className="pb-0">
                Add or edit the add-ons themselves
              </Typography>
              <Typography variant="supporting" className="block">
                Add-ons are products with the add-on type. Create and edit them in Products, then
                bundle them into a collection here.
              </Typography>
            </div>
          </div>
          {addOns.length ? (
            <div className="flex flex-wrap gap-1.5 pl-12">
              {visibleAddOns.map((addOn) => (
                <AddOnChip key={addOn.id ?? addOn.name} item={addOn} withMedia />
              ))}
              {hiddenCount > 0 ? <AddOnOverflowChip>+{hiddenCount} more</AddOnOverflowChip> : null}
            </div>
          ) : null}
        </div>
        <AdminHeaderActionButton variant="outline" asChild>
          <Link {...path.products.index().search({ type: 'add-ons' }).link}>
            Manage in Products
            <ChevronRight className="size-4" />
          </Link>
        </AdminHeaderActionButton>
      </div>
    </section>
  )
}

export function AddOnSetsIndexPage() {
  const { orgId } = Route.useParams()
  const path = usePathHelper()
  const { data: addOnSets = [], isLoading } = useAddOnSets({ organizationId: orgId })
  const [search, setSearch] = useState('')
  const normalizedSearch = search.trim().toLowerCase()
  const filteredAddOnSets = normalizedSearch
    ? addOnSets.filter((addOnSet) =>
        [addOnSet?.name, addOnSet?.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch))
      )
    : addOnSets
  const addOnSetCountText = normalizedSearch
    ? `${filteredAddOnSets.length} of ${addOnSets.length}`
    : `${addOnSets.length} total`
  const uniqueAddOns = getUniqueAddOns(addOnSets)
  const emptyState = normalizedSearch ? (
    <Typography variant="supporting">No add-on collections found for that search.</Typography>
  ) : (
    <div className="space-y-3">
      <div className="space-y-1">
        <Typography variant="h3">No add-on collections yet</Typography>
        <Typography variant="supporting">
          Add-on collections make it easier to reuse common extras like chocolates, balloons, and
          plush gifts across multiple collections.
        </Typography>
      </div>
      <Can I="create" a="products">
        <AdminHeaderActionButton asChild>
          <Link {...path.addOnSets.create().link}>
            <Plus className="size-4" /> Create your first collection
          </Link>
        </AdminHeaderActionButton>
      </Can>
    </div>
  )

  return (
    <div className="w-full max-w-none space-y-4">
      <ProductCatalogNav active="add-ons" />
      <AdminPageHeader
        icon={PackagePlus}
        title="Add-ons"
        description="Bundle order extras, then choose which product collections they appear with."
        actions={
          <Can I="create" a="products">
            <AdminHeaderActionButton asChild>
              <Link {...path.addOnSets.create().link}>
                <Plus className="size-4" /> New Add-on Collection
              </Link>
            </AdminHeaderActionButton>
          </Can>
        }
      />

      <section className="space-y-4 rounded-lg border border-border/70 bg-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Typography variant="cardTitle" className="pb-0">
                Add-on collections
              </Typography>
              <Badge variant="brand" size="chip">
                {addOnSets.length}
              </Badge>
            </div>
            <Typography variant="supporting" className="block max-w-3xl">
              Bundle add-ons, then choose the collections where shoppers should see them.
            </Typography>
          </div>
          <div className="w-full sm:w-auto">
            <IndexTableSearchBox
              value={search}
              onCommit={setSearch}
              countText={addOnSetCountText}
              isFetching={isLoading}
              placeholder="Search add-on collections..."
            />
          </div>
        </div>

        {isLoading ? (
          <Typography variant="supporting">Loading add-on collections...</Typography>
        ) : filteredAddOnSets.length ? (
          <div className="space-y-3">
            {filteredAddOnSets.map((addOnSet) => (
              <AddOnSetCard key={addOnSet.id} addOnSet={addOnSet} path={path} />
            ))}
          </div>
        ) : (
          emptyState
        )}
      </section>

      <ManageAddOnProductsPanel addOns={uniqueAddOns} path={path} />
    </div>
  )
}
